import { PARTNER_PORTAL_ROUTE } from 'config'
import { selectors as apiSelectors } from 'lp-redux-api'
import { selectors as globalSelectors } from 'global-reducer'
import * as apiActions from 'api-actions'
import { selectors as partnerProfileSelectors } from './reducer'

const STORAGE_KEY_PREFIX = 'partnerProfileCompletion:'

// Education Contact (2) and Executive Director (7) — required for profile completion.
export const REQUIRED_CONTACT_POSITION_IDS = [2, 7]

export const SECTION_KEYS = {
  ORGANIZATION: 'organization_details',
  PROGRAMS: 'programs',
  PARTNERSHIPS: 'school_partnerships',
  CONTACTS: 'staff_contact',
  ASSOCIATED_TA: 'associated_teaching_artist',
}

export const SECTION_META = {
  [SECTION_KEYS.ORGANIZATION]: {
    label: 'Organization Details',
    tooltip: 'All fields are required to complete this section.',
    route: PARTNER_PORTAL_ROUTE + '/profile/organization',
  },
  [SECTION_KEYS.PROGRAMS]: {
    label: 'Programs',
    tooltip: 'You need to add one program to complete this section.',
    route: PARTNER_PORTAL_ROUTE + '/profile/programs',
  },
  [SECTION_KEYS.PARTNERSHIPS]: {
    label: 'School Partnerships',
    tooltip:
      'To complete this section, select whether or not you are currently looking for partnerships. You do not need to add partnerships to complete this section.',
    route: PARTNER_PORTAL_ROUTE + '/profile/partnerships',
  },
  [SECTION_KEYS.CONTACTS]: {
    label: 'Contacts',
    tooltip:
      'Complete both the Education Contact and Executive Director contact forms to finish this section.',
    route: PARTNER_PORTAL_ROUTE + '/profile/contacts',
  },
  [SECTION_KEYS.ASSOCIATED_TA]: {
    label: 'Associated Teaching Artists',
    tooltip: 'Add one teaching artist to complete this section.',
    route: PARTNER_PORTAL_ROUTE + '/profile/associated-teaching-artist',
  },
}

export function getSectionsForCommunity(community) {
  const base = [
    SECTION_KEYS.ORGANIZATION,
    SECTION_KEYS.PROGRAMS,
    SECTION_KEYS.PARTNERSHIPS,
    SECTION_KEYS.CONTACTS,
  ]
  return community &&
    community.teachingArtist &&
    community.teachingArtist.enabled
    ? [...base, SECTION_KEYS.ASSOCIATED_TA]
    : base
}

// Returns the percent threshold for the given count of completed sections,
// snapped to the canonical scale enforced by the backend (25/50/75/100 for
// 4 sections, 20/40/60/80/100 for 5 sections).
export function computePercent(completedCount, totalCount) {
  if (!totalCount) return 0
  if (completedCount <= 0) return 0
  return Math.round((completedCount / totalCount) * 100)
}

// `fetchPartnershipQuestion` stores `data` only (see api successDataPath: 'data').
// Treat a list or single object; complete the partnerships section when nothing
// has type `partnership_question`.
export function hasTypedPartnershipQuestionPayload(stored) {
  if (stored == null) return false
  if (Array.isArray(stored)) {
    return stored.some(
      (item) => item && String(item.type) === 'partnership_question'
    )
  }
  if (typeof stored === 'object') {
    return String(stored.type) === 'partnership_question'
  }
  return false
}

export function buildPartnershipQuestionCompletionOptions(state) {
  if (!state) return {}
  const partnershipQuestionPayload =
    partnerProfileSelectors.partnershipQuestion(state)
  const loading = apiSelectors.isLoading(
    state,
    apiActions.fetchPartnershipQuestion
  )
  const failed = apiSelectors.isFailure(
    state,
    apiActions.fetchPartnershipQuestion
  )
  const fetchDone =
    !loading && (failed || partnershipQuestionPayload !== undefined)
  const currentSchoolYear = globalSelectors.currentSchoolYear(state)
  return {
    partnershipQuestionPayload,
    partnershipQuestionFetchDone: fetchDone,
    partnershipQuestionFetchFailed: failed,
    currentSchoolYearId: currentSchoolYear ? currentSchoolYear.id : null,
  }
}

// Canonical "is this tab complete?" rule per AC3.
// The exact field shape mirrors the OrganizationPortalSerializer payload.
export function isSectionComplete(sectionKey, partner, options = {}) {
  if (!partner) return false
  const {
    isPartnershipQuestionUnavailable = false,
    partnershipQuestionPayload,
    partnershipQuestionFetchDone = false,
    partnershipQuestionFetchFailed = false,
  } = options

  switch (sectionKey) {
    case SECTION_KEYS.ORGANIZATION: {
      const detail = partner.detail || {}
      const disciplines = partner.disciplines || []
      return Boolean(
        partner.name &&
          detail.website &&
          detail.phone &&
          partner.logo &&
          disciplines.length >= 1
      )
    }
    case SECTION_KEYS.PROGRAMS: {
      const { currentSchoolYearId } = options
      if (!Array.isArray(partner.programs)) return false
      // Only programs from the most recent (current) school year count toward
      // completion. Fall back to any program if the current year is unknown.
      if (currentSchoolYearId == null) return partner.programs.length >= 1
      return partner.programs.some(
        (program) => program.schoolYearId === currentSchoolYearId
      )
    }
    case SECTION_KEYS.PARTNERSHIPS:
      if (isPartnershipQuestionUnavailable) return true
      if (
        partnershipQuestionFetchDone &&
        !partnershipQuestionFetchFailed &&
        !hasTypedPartnershipQuestionPayload(partnershipQuestionPayload)
      ) {
        return true
      }
      // "Yes", "No" (false), and an unanswered value (null/undefined) all
      // complete this section — partners have no follow-up interests to fill
      // in. The toggle renders a null value identically to "No" (off), so a
      // saved "No" must be honored even when the underlying value is still null.
      return true
    case SECTION_KEYS.CONTACTS: {
      const contacts = partner.contacts || []
      return REQUIRED_CONTACT_POSITION_IDS.every((positionId) => {
        const contact = contacts.find(
          (c) => Number(c.positionId) === positionId
        )
        return Boolean(
          contact &&
            contact.firstName &&
            contact.lastName &&
            contact.phone &&
            contact.email
        )
      })
    }
    case SECTION_KEYS.ASSOCIATED_TA: {
      const partnerships = partner.partnerships || []
      return partnerships.some((p) => p.teachingArtistId)
    }
    default:
      return false
  }
}

// Returns the *full* set of completed section keys for the given partner,
// using `isSectionComplete` to re-derive each section.
export function deriveCompletedSections(partner, community, options = {}) {
  const sections = getSectionsForCommunity(community)
  return sections.filter((sectionKey) =>
    isSectionComplete(sectionKey, partner, options)
  )
}

function storageKey(partnerId) {
  return `${STORAGE_KEY_PREFIX}${partnerId}`
}

export function getStoredProfileCompletion(partnerId) {
  if (!partnerId || typeof window === 'undefined') return null

  try {
    const raw = window.localStorage.getItem(storageKey(partnerId))
    if (!raw) return null

    const parsed = JSON.parse(raw)
    return {
      sectionsCompleted: Array.isArray(parsed.sectionsCompleted)
        ? parsed.sectionsCompleted
        : [],
      percentCompletion: Number(parsed.percentCompletion) || 0,
    }
  } catch (e) {
    return null
  }
}

export function setStoredProfileCompletion(partnerId, status) {
  if (!partnerId || typeof window === 'undefined') return

  const payload = {
    sectionsCompleted: status.sectionsCompleted || [],
    percentCompletion: status.percentCompletion || 0,
  }
  window.localStorage.setItem(storageKey(partnerId), JSON.stringify(payload))
}

// Convenience helper for serializing the bar's view-model.
export function buildBarModel(partner, community, options = {}) {
  const sections = getSectionsForCommunity(community)
  const completedSections = deriveCompletedSections(
    partner,
    community,
    options
  ).filter((sectionKey) => sections.includes(sectionKey))
  const completedSet = new Set(completedSections)
  const orderedCompletedSections = sections.filter((sectionKey) =>
    completedSet.has(sectionKey)
  )
  const orderedIncompleteSections = sections.filter(
    (sectionKey) => !completedSet.has(sectionKey)
  )
  const orderedSections = [
    ...orderedCompletedSections,
    ...orderedIncompleteSections,
  ]
  const percent = computePercent(completedSections.length, sections.length)

  return {
    sections: orderedSections.map((key) => ({ key, ...SECTION_META[key] })),
    completedSections: orderedCompletedSections,
    percent,
    totalCount: sections.length,
  }
}
